import * as React from 'react';
import { useState } from 'react';
import * as API from '../../Utils/API.js';
import { AdvisingPurchase } from '../../Types/Advising';
import { DismissibleModal } from '../../Components/Modal';
import { htmlIf } from '../../Utils/HTML';
import { formatUSDWithCents } from '../../Utils/Currency';

type Props =
  { purchase: AdvisingPurchase
  , isAdmin: boolean
  }

enum Modal
  { NoModal
  , LogWorkModal
  }

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

const PackageCard = (props: Props) => {
  const [purchase, setPurchase] = useState(props.purchase);
  const [isExpanded, setIsExpanded] = useState(true);

  const [modal, setModal] = useState(Modal.NoModal);
  const [showModalErrors, setShowModalErrors] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [workDescription, setWorkDescription] = useState('');
  const [workDurationMinutes, setWorkDurationMinutes] = useState(0);

  function logWork() {
    if (workDescription.length > 0 && workDurationMinutes > 0) {
      setIsSubmitting(true);
      const body =
        { advisingPurchaseId: purchase.id
        , notes: workDescription
        , numMinutes: workDurationMinutes
        }
      API.post("advisor_clients_log_work_path", body).then(function (result) {
        if (result['error']) {
        } else {
          setPurchase(result['purchase']);
        }
        setModal(Modal.NoModal);
        setWorkDescription('');
        setWorkDurationMinutes(0);
        setIsSubmitting(false);
      })
    } else {
      setShowModalErrors(true);
    }
  }

  const ViewModal = () => {
    switch(modal) {
      case Modal.NoModal:
        return null
      case Modal.LogWorkModal:
        return (
          <DismissibleModal
            title={<h4>Log Work</h4>}
            body={
              <div>
                <div className="">
                  <h6 className="fw-bold">Description</h6>
                  <textarea className="form-control"
                    placeholder="Enter a brief description of the work you’re logging…"
                    rows={5}
                    value={workDescription}
                    onChange={(event) => setWorkDescription(event.target.value)}
                  />
                  {htmlIf(showModalErrors && !workDescription.length,
                    <div className="fs-sm mt-1 text-danger fw-bold">Please enter a description.</div>
                  )}
                </div>
                <div className="mt-3">
                  <h6 className="fw-bold">Duration (minutes)</h6>
                  <input className="form-control"
                    type="number"
                    value={workDurationMinutes}
                    onChange={(event) => setWorkDurationMinutes(parseInt(event.target.value))}
                    placeholder="Enter a whole number of minutes…"
                  />
                  {htmlIf(showModalErrors && !(workDurationMinutes > 0),
                    <div className="fs-sm mt-1 text-danger fw-bold">Please enter a number greater than 0.</div>
                  )}
                </div>
              </div>
            }
            footer={
              <>
                <button className="btn btn-outline-danger w-100 w-sm-auto mb-2 mb-sm-0"
                  onClick={() => setModal(Modal.NoModal)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button className="btn btn-primary w-100 w-sm-auto ms-sm-2"
                  onClick={() => logWork()}
                  disabled={isSubmitting}
                >
                  Save
                </button>
              </>
            }
            onDismiss={() => setModal(Modal.NoModal)}
          />
        )
      default:
        return null
    }
  }

  const Log = (purchase: AdvisingPurchase) => {
    const formatHoursMinutes = (minutes) => {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${String(hours).padStart(1, "0")}:${String(remainingMinutes).padStart(2, "0")}`;
    };

    const sumMinutesLast24Hours = purchase.offlineWorkItems
      .filter(({ createdAt }) => new Date(createdAt) > new Date(Date.now() - 24 * 60 * 60 * 1000))
      .reduce((sum, { minutesLogged }) => sum + minutesLogged, 0);

    const sumMinutesLast7Days = purchase.offlineWorkItems
      .filter(({ createdAt }) => new Date(createdAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))
      .reduce((sum, { minutesLogged }) => sum + minutesLogged, 0);

    const sumMinutesSinceStart = purchase.offlineWorkItems.reduce((sum, { minutesLogged }) => sum + minutesLogged, 0);

    return (
      <div className="">
        <div className="d-flex align-items-center justify-content-between">
          <h5 className="mb-0">Package Offline Work Log</h5>
          {htmlIf(props.purchase.minutesRemaining > 0,
            <button className="btn btn-primary px-3" onClick={() => setModal(Modal.LogWorkModal)}>
              Log Offline Work
            </button>
          )}
        </div>
        <div className="row mt-2">
          <div className="col-md-4">
            <div className="fw-bold">{formatHoursMinutes(sumMinutesLast24Hours)} hrs</div>
            <div className="">Last 24 hours</div>
          </div>
          <div className="col-md-4">
            <div className="fw-bold">{formatHoursMinutes(sumMinutesLast7Days)} hrs</div>
            <div className="">Last 7 days</div>
          </div>
          <div className="col-md-4">
            <div className="fw-bold">{formatHoursMinutes(sumMinutesSinceStart)} hrs</div>
            <div className="">Since start</div>
          </div>
        </div>
        <div className="mt-2 table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Date</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {purchase.offlineWorkItems
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map((item, index) => (
                  <tr className={`t--hourly-work-item-id-${item.id}`} key={index}>
                    <td>{item.notes}</td>
                    <td className="text-nowrap">{formatDate(item.createdAt)}</td>
                    <td className="text-nowrap">{formatHoursMinutes(item.minutesLogged)}</td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div className="card card-body py-0 rounded-2 mb-3">
      <div key={purchase.id} className={`t--purchase-id-${purchase.id}`}>
        {ViewModal()}
        <div className="px-0 py-2">
          <div className="d-flex justify-content-between align-items-center cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
            <div className="d-flex align-items-center">
              <div className="fw-semibold">
                Package: {purchase.packageName}
              </div>
              {htmlIf(props.purchase.minutesRemaining === 0,
                <div className="badge bg-faded-danger ms-2 text-danger">No hours remaining</div>
              )}
            </div>
            <i className={`fs-xl ${isExpanded ? 'ai-chevron-up' : 'ai-chevron-down'}`} />
          </div>
          {htmlIf(isExpanded,
            <div className="mt-3">
              <div className="mb-1">Purchased on: {formatDate(purchase.paidAt)}</div>
              <div className="mb-1">Package Price: {formatUSDWithCents(purchase.amountCents)}</div>
              <div className="mb-1">Refund Period Expires on: {formatDate(purchase.refundPeriodExpiresAt)}</div>
              {htmlIf(props.isAdmin,
                <div className="mb-2 bg-gray px-2 py-1 rounded-2">
                  Advising Purchase ID: {purchase.id}
                </div>
              )}
              <div className="">
                {purchase.minutesRemaining} minutes remaining of {purchase.minutesPurchased} minutes purchased
                (includes both meetings & offline work).
              </div>
              <div className="mt-2">
                {Log(purchase)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PackageCard
